'use client';

// ============================================================================
// ROADMAP VIEW
// Quarter-based roadmap with epics, milestones, sprints, and timeline
// visualization - comparable to Jira Roadmaps / Azure DevOps Plans
// Enhanced: December 2025 - Proper sprint integration from work_item_sprints
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Layers,
  Target,
  Flag,
  Link,
  Plus,
  Filter,
  Maximize2,
  Check,
  Clock,
  AlertCircle,
  Users,
  Activity
} from 'lucide-react';
import { authService } from '@/lib/authService';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { createLogger } from '@/lib/debugLogger';

const logger = createLogger('RoadmapView');

interface RoadmapViewProps {
  permissions: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
}

// Extended to include sprint-specific fields from work_item_sprints
interface RoadmapItem extends WorkItem {
  children?: RoadmapItem[];
  dependencies?: string[];
  progress?: number;
  // Sprint-specific fields
  goal?: string;
  capacity_points?: number;
  velocity?: number;
  items_count?: number;
  total_points?: number;
  burndown?: any[];
  // For milestones without start_date
  is_milestone_marker?: boolean;
}

interface Quarter {
  year: number;
  quarter: number;
  label: string;
  startDate: Date;
  endDate: Date;
}

type ViewLevel = 'epic' | 'milestone' | 'sprint' | 'feature';

export default function RoadmapView({ permissions }: RoadmapViewProps) {
  // State
  const [epics, setEpics] = useState<RoadmapItem[]>([]);
  const [milestones, setMilestones] = useState<RoadmapItem[]>([]);
  const [sprints, setSprints] = useState<RoadmapItem[]>([]);
  const [features, setFeatures] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewLevel, setViewLevel] = useState<ViewLevel>('epic');
  const [selectedItem, setSelectedItem] = useState<RoadmapItem | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCompleted, setShowCompleted] = useState(true); // Show completed by default to see data

  // Generate quarters for timeline (current - 1 quarter to current + 3 quarters)
  const quarters = useMemo(() => {
    const result: Quarter[] = [];
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;

    // Start from previous quarter
    let startYear = currentYear;
    let startQuarter = currentQuarter - 1;
    if (startQuarter < 1) {
      startQuarter = 4;
      startYear--;
    }

    // Generate 5 quarters
    for (let i = 0; i < 5; i++) {
      const q = startQuarter + i;
      const year = q > 4 ? startYear + Math.floor((q - 1) / 4) : startYear;
      const quarter = ((q - 1) % 4) + 1;

      const startMonth = (quarter - 1) * 3;
      const endMonth = startMonth + 2;

      result.push({
        year,
        quarter,
        label: `Q${quarter} ${year}`,
        startDate: new Date(year, startMonth, 1),
        endDate: new Date(year, endMonth + 1, 0)
      });
    }

    return result;
  }, []);

  useEffect(() => {
    loadRoadmapData();
  }, [viewLevel, showCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadRoadmapData() {
    try {
      setLoading(true);
      const token = authService.getTokens()?.access_token;
      
      if (!token) {
        throw new Error('Authentication required');
      }

      // Load epics from work_items (no module_context filter to get all)
      const epicsResponse = await fetch(
        `/api/v1/work-items?item_type=epic${showCompleted ? '' : '&status_not=completed,closed'}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let epicsData: RoadmapItem[] = [];
      if (epicsResponse.ok) {
        const data = await epicsResponse.json();
        epicsData = (data.items || data.data || []).map((item: WorkItem) => ({
          ...item,
          progress: calculateProgress(item)
        }));
      }

      // Load milestones from work_items (no module_context filter to get all)
      const milestonesResponse = await fetch(
        `/api/v1/work-items?item_type=milestone${showCompleted ? '' : '&status_not=completed,closed'}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let milestonesData: RoadmapItem[] = [];
      if (milestonesResponse.ok) {
        const data = await milestonesResponse.json();
        milestonesData = (data.items || data.data || []).map((item: WorkItem) => ({
          ...item,
          progress: calculateProgress(item),
          is_milestone_marker: !item.start_date // Flag milestones without start date
        }));
      }

      // Load sprints from work_item_sprints table via dedicated API
      const sprintsResponse = await fetch(
        `/api/v1/work-items/sprints?include_burndown=true${showCompleted ? '' : '&status_not=completed,closed'}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let sprintsData: RoadmapItem[] = [];
      if (sprintsResponse.ok) {
        const data = await sprintsResponse.json();
        // Transform sprint data to RoadmapItem format
        sprintsData = (data.data || []).map((sprint: any) => ({
          id: sprint.id,
          title: sprint.name,
          item_number: `SPR-${sprint.id}`,
          status: sprint.status,
          start_date: sprint.start_date,
          due_date: sprint.end_date,
          description: sprint.goal,
          goal: sprint.goal,
          capacity_points: sprint.capacity_points,
          velocity: sprint.velocity,
          items_count: sprint.items_count,
          total_points: sprint.total_points,
          burndown: sprint.burndown,
          item_type: 'sprint',
          progress: sprint.velocity ? Math.round((sprint.velocity / (sprint.capacity_points || 1)) * 100) : 0
        }));
      }

      setEpics(epicsData);
      setMilestones(milestonesData);
      setSprints(sprintsData);

      // Load features from work_items
      const featuresResponse = await fetch(
        `/api/v1/work-items?item_type=feature${showCompleted ? '' : '&status_not=completed,closed'}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let featuresData: RoadmapItem[] = [];
      if (featuresResponse.ok) {
        const data = await featuresResponse.json();
        featuresData = (data.items || data.data || []).map((item: WorkItem) => ({
          ...item,
          progress: calculateProgress(item)
        }));
      }

      setFeatures(featuresData);

    } catch (error) {
      logger.error('Failed to load roadmap data:', error);
    } finally {
      setLoading(false);
    }
  }

  function calculateProgress(item: WorkItem): number {
    // Calculate progress from custom_fields or status
    if (item.custom_fields?.progress) {
      return item.custom_fields.progress;
    }
    if (item.status === 'completed' || item.status === 'closed') {
      return 100;
    }
    if (item.status === 'in_progress' || item.status === 'in-progress') {
      return 50;
    }
    return 0;
  }

  const getItemsForView = (): RoadmapItem[] => {
    switch (viewLevel) {
      case 'epic':
        return epics;
      case 'milestone':
        return milestones;
      case 'sprint':
        return sprints;
      case 'feature':
        return features;
      default:
        return [];
    }
  };

  const getItemPosition = (item: RoadmapItem) => {
    const timelineStart = quarters[0].startDate;
    const timelineEnd = quarters[quarters.length - 1].endDate;
    const totalDuration = timelineEnd.getTime() - timelineStart.getTime();

    // Handle milestones that only have due_date (show as diamond marker)
    if (item.is_milestone_marker && item.due_date) {
      const dueDate = new Date(item.due_date);
      const itemPosition = dueDate.getTime() - timelineStart.getTime();
      const left = (itemPosition / totalDuration) * 100;
      
      // Return marker position (very small width to show as point)
      if (left >= 0 && left <= 100) {
        return { left, width: 0.5, isMarker: true };
      }
      return null;
    }

    // Standard range items (epics, sprints with both dates)
    if (!item.start_date || !item.due_date) {
      return null;
    }

    const startDate = new Date(item.start_date);
    const endDate = new Date(item.due_date);

    // Calculate position as percentage
    const itemStart = startDate.getTime() - timelineStart.getTime();
    const itemDuration = endDate.getTime() - startDate.getTime();

    const left = Math.max(0, (itemStart / totalDuration) * 100);
    const width = Math.min(100 - left, (itemDuration / totalDuration) * 100);

    return { left, width, isMarker: false };
  };

  const getStatusColor = (status?: string, priority?: string) => {
    const statusColors: Record<string, string> = {
      'completed': 'bg-green-500',
      'closed': 'bg-green-500',
      'in_progress': 'bg-blue-500',
      'in-progress': 'bg-blue-500',
      'blocked': 'bg-red-500',
      'open': priority === 'high' || priority === 'critical' ? 'bg-orange-500' : 'bg-gray-400',
      'planned': 'bg-purple-500'
    };
    return statusColors[status ?? ''] || 'bg-gray-400';
  };

  const handleItemClick = (item: RoadmapItem) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading roadmap...</p>
        </div>
      </div>
    );
  }

  const items = getItemsForView();

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* View Level Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewLevel('epic')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewLevel === 'epic'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Layers className="w-4 h-4" />
                Epics
              </button>
              <button
                onClick={() => setViewLevel('milestone')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewLevel === 'milestone'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Flag className="w-4 h-4" />
                Milestones
              </button>
              <button
                onClick={() => setViewLevel('sprint')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewLevel === 'sprint'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Target className="w-4 h-4" />
                Sprints
              </button>
              <button
                onClick={() => setViewLevel('feature')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                  viewLevel === 'feature'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Activity className="w-4 h-4" />
                Features
              </button>
            </div>

            {/* Show Completed Toggle */}
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={showCompleted}
                onChange={(e) => setShowCompleted(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show Completed
            </label>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span>{items.length} {viewLevel}s</span>
            {items.filter(i => i.status === 'in_progress' || i.status === 'in-progress').length > 0 && (
              <>
                <span>•</span>
                <span className="text-blue-600 dark:text-blue-400">
                  {items.filter(i => i.status === 'in_progress' || i.status === 'in-progress').length} in progress
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-auto relative">
        <div className="min-w-[1200px]">
          {/* Quarter Headers */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
            <div className="flex">
              {/* Item Labels Column */}
              <div className="w-64 border-r border-gray-200 dark:border-gray-700 p-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {viewLevel.charAt(0).toUpperCase() + viewLevel.slice(1)}
                </span>
              </div>
              {/* Quarter Columns */}
              <div className="flex-1 flex">
                {quarters.map(quarter => {
                  const today = new Date();
                  const isCurrentQuarter = 
                    today >= quarter.startDate && today <= quarter.endDate;
                  
                  return (
                    <div
                      key={`${quarter.year}-Q${quarter.quarter}`}
                      className={`flex-1 border-r border-gray-200 dark:border-gray-700 p-4 ${
                        isCurrentQuarter ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                      }`}
                    >
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        {quarter.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {quarter.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {quarter.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Roadmap Items */}
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {items.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-gray-400">No {viewLevel}s found</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {showCompleted ? 'Try unchecking "Show Completed"' : 'Create your first ' + viewLevel + ' to get started'}
                  </p>
                </div>
              </div>
            ) : (
              items.map(item => {
                const position = getItemPosition(item);
                
                return (
                  <div key={item.id} className="flex hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    {/* Item Label */}
                    <div className="w-64 border-r border-gray-200 dark:border-gray-700 p-4">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <div className="text-xs font-mono text-gray-500 mb-1">
                            {item.item_number}
                          </div>
                          <button
                            onClick={() => handleItemClick(item)}
                            className="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 text-left line-clamp-2"
                          >
                            {item.title}
                          </button>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              item.status === 'completed' || item.status === 'closed'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : item.status === 'in_progress' || item.status === 'in-progress' || item.status === 'active'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : item.status === 'planning'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {item.status}
                            </span>
                            {/* Sprint specific info */}
                            {item.items_count !== undefined && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                {item.items_count} items
                              </span>
                            )}
                            {/* Milestone due date */}
                            {item.is_milestone_marker && item.due_date && (
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Flag className="w-3 h-3" />
                                {new Date(item.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Timeline Bar */}
                    <div className="flex-1 p-4 relative">
                      <div className="flex h-full items-center">
                        <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded relative">
                          {position && position.isMarker ? (
                            /* Milestone Marker (diamond shape) */
                            <div
                              className={`absolute -top-1.5 cursor-pointer transform -translate-x-1/2 ${getStatusColor(item.status, item.priority)}`}
                              style={{
                                left: `${position.left}%`,
                                width: '16px',
                                height: '16px',
                                transform: 'translateX(-50%) rotate(45deg)',
                                borderRadius: '2px'
                              }}
                              onClick={() => handleItemClick(item)}
                              title={`${item.title} - Due: ${new Date(item.due_date!).toLocaleDateString()}`}
                            />
                          ) : position && (
                            /* Range Bar (epics, sprints) */
                            <div
                              className={`absolute h-full rounded cursor-pointer hover:opacity-90 transition-opacity ${getStatusColor(item.status, item.priority)}`}
                              style={{
                                left: `${position.left}%`,
                                width: `${Math.max(position.width, 1)}%`,
                                minWidth: '8px'
                              }}
                              onClick={() => handleItemClick(item)}
                              title={`${item.title}${item.progress !== undefined ? ` (${item.progress}% complete)` : ''}`}
                            >
                              {/* Progress Indicator */}
                              {item.progress !== undefined && item.progress > 0 && (
                                <div
                                  className="absolute inset-y-0 left-0 bg-green-600 rounded opacity-75"
                                  style={{ width: `${item.progress}%` }}
                                />
                              )}
                              {/* Sprint Points Badge */}
                              {item.total_points !== undefined && (
                                <div className="absolute -top-5 left-0 text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                  {item.total_points} pts
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Today Indicator - positioned absolutely within the scrollable area */}
        {quarters.length > 0 && (() => {
          const today = new Date();
          const timelineStart = quarters[0].startDate.getTime();
          const timelineEnd = quarters[quarters.length - 1].endDate.getTime();
          const todayPosition = ((today.getTime() - timelineStart) / (timelineEnd - timelineStart)) * 100;
          
          // Only show if today is within the timeline range
          if (todayPosition >= 0 && todayPosition <= 100) {
            return (
              <div 
                className="absolute top-[100px] bottom-0 pointer-events-none z-20" 
                style={{ left: `calc(16rem + ${todayPosition}%)` }}
              >
                <div className="w-0.5 h-full bg-red-500 opacity-50"></div>
                <div className="absolute -top-2 -left-2 w-4 h-4 bg-red-500 rounded-full"></div>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <WorkItemDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedItem(null);
          }}
          workItem={selectedItem}
          onUpdate={() => loadRoadmapData()}
        />
      )}
    </div>
  );
}
